// Dependencies
import { cloneDeep, flatten, isEmpty } from 'lodash'
import moment from 'moment'

import { findMatchingProduct, getAllProductsAtQuays } from './general'

/**
 * Create dataset / items to render in resourcePlanning component
 * @param terminalVisits
 */
export function createResourcePlanningDataset(terminalVisits, startTime, endTime, config) {
  if (!terminalVisits || !terminalVisits.length) {
    return []
  }

  const products = getAllProductsAtQuays(config.quays)

  // Set the initial values of the start the current month in viewport
  const startSelection = moment(startTime)
  const endSelection = moment(endTime)

  // get the closest starting point in the past, so if the time is 02/01/2017 00:00 it will be 01/01/2017 22:00
  const closestStartingShift = findClosestStartingShift(startSelection, config.shifts)

  let startingPoint = startSelection.clone().hours(closestStartingShift.hour).minutes(0)

  // the closest shift was the previous day
  if (closestStartingShift.hour > startSelection.hours()) {
    startingPoint = cloneDeep(startSelection).subtract(1, 'days').hours(closestStartingShift.hour).minutes(0)
  }

  // get the closest end point in the future, so if the time is 12/01/2017 00:00 it will be 12/01/2017 06:00
  const closestEndingShift = findClosestEndingShift(endSelection, config.shifts)

  let endPoint = cloneDeep(endSelection).hours(closestEndingShift.hour).minutes(0)

  // the closest shift was the next day
  if (closestEndingShift.hour < endSelection.hours()) {
    endPoint = cloneDeep(endSelection).add(1, 'day').hours(closestEndingShift.hour).minutes(0)
  }

  const shiftDuration = 8
  const shifts = createShifts(startingPoint, endPoint, shiftDuration)

  return shifts.map(shift => {
    const ordersInShift = flatten(
      terminalVisits.map(terminalVisit => {
        return terminalVisit.orders.filter(order => isInShift(order, shift))
      })
    )
    const manning = calculateManPower(shift, ordersInShift, products)

    return {
      shiftStart: shift.shiftStart,
      shiftEnd: shift.shiftEnd,
      manningTotal: manning.manningTotal,
      manningMap: manning.manningMap
    }
  })
}

/**
 * Finds the past shift closest to a specified start time
 * @param startSelection start time
 * @param shifts array containing shifts configuration
 */
export function findClosestStartingShift(startSelection, shifts) {
  const closestShift = {}

  shifts.forEach(shift => {
    // time difference calculated for shifts in the past
    const shiftTimeDifference =
      startSelection.hours() > shift.hour
        ? startSelection.hours() - shift.hour
        : 24 + startSelection.hours() - shift.hour

    if (isEmpty(closestShift) || shiftTimeDifference < closestShift.timeDifference) {
      closestShift.hour = shift.hour
      closestShift.timeDifference = shiftTimeDifference
    }
  })
  return closestShift
}

/**
 * Finds the future shift closest to a specified start time
 * @param endSelection end time
 * @param shifts array containing shifts configuration
 */
export function findClosestEndingShift(endSelection, shifts) {
  const closestShift = {}

  shifts.forEach(shift => {
    // time difference calculated for shifts in the future
    const shiftTimeDifference =
      shift.hour > endSelection.hours() ? shift.hour - endSelection.hours() : 24 + shift.hour - endSelection.hours()

    if (isEmpty(closestShift) || shiftTimeDifference < closestShift.timeDifference) {
      closestShift.hour = shift.hour
      closestShift.timeDifference = shiftTimeDifference
    }
  })

  return closestShift
}

function createShifts(startingPoint, endPoint, shiftDuration) {
  const result = []
  const shiftDurationInMs = shiftDuration * 3600000
  let startingPointInMs = startingPoint.valueOf() + shiftDurationInMs
  const endPointInMs = endPoint.valueOf()

  for (startingPointInMs; startingPointInMs <= endPointInMs; startingPointInMs += shiftDurationInMs) {
    result.push({
      shiftStart: startingPointInMs - shiftDurationInMs,
      shiftEnd: startingPointInMs
    })
  }

  return result
}

function isInShift(order, shift) {
  return order.cargoEnd >= shift.shiftStart && order.cargoStart < shift.shiftEnd
}

function calculateManPower(shift, ordersInShift, productInformation) {
  let manningTotal = 0
  const manningMap = {}

  const hours = getStartAndEndForEveryHourInShift(shift)

  ordersInShift.forEach(order => {
    const matchingProduct = findMatchingProduct(order.product, order.operation, productInformation)

    hours.forEach(hour => {
      if (order.cargoEnd > hour.shiftStart && order.cargoStart < hour.shiftEnd) {
        if (matchingProduct) {
          const key = matchingProduct.product + ' - ' + (matchingProduct.isUnloading ? 'unloading' : 'loading')

          if (manningMap[key]) {
            manningMap[key].manning += matchingProduct.manning
          } else {
            manningMap[key] = {
              manning: matchingProduct.manning,
              product: matchingProduct.product,
              color: matchingProduct.color,
              isUnloading: matchingProduct.isUnloading
            }
          }

          manningTotal += matchingProduct.manning
        }
      }
    })
  })

  return { manningTotal, manningMap }
}

function getStartAndEndForEveryHourInShift(shift) {
  const hour = 3600000
  const totalTimeInShift = shift.shiftEnd - shift.shiftStart
  const hoursInShift = totalTimeInShift / hour
  const hours = []

  for (let i = 1; i <= hoursInShift; i++) {
    hours.push({
      shiftStart: shift.shiftStart + hour * (i - 1),
      shiftEnd: shift.shiftStart + hour * i
    })
  }

  return hours
}
